/** Map top-row and numpad 1–3 onto Digit1–3 so later tool slots share one path. */
export function digitCode(event: KeyboardEvent): string {
  const key = event.key;
  if (key === '1' || key === '2' || key === '3') {
    return `Digit${key}`;
  }
  switch (event.code) {
    case 'Numpad1':
      return 'Digit1';
    case 'Numpad2':
      return 'Digit2';
    case 'Numpad3':
      return 'Digit3';
    default:
      return event.code;
  }
}

export const PAD_DEADZONE = 0.2;
/** movementX/Y-equivalent at full right stick, applied once per consumeLook. */
export const PAD_LOOK_SCALE = 16;
export const PAD_AXIS_LX = 0;
export const PAD_AXIS_LY = 1;
export const PAD_AXIS_RX = 2;
export const PAD_AXIS_RY = 3;
/** A / South. Jump. */
export const PAD_SOUTH = 0;
/** LB / L1. Sneak hold. */
export const PAD_LB = 4;

export type PadButtonLike =
  | boolean
  | number
  | { pressed?: boolean; value?: number }
  | null
  | undefined;

export type PadSample = {
  moveX: number;
  moveZ: number;
  lookX: number;
  lookY: number;
  jump: boolean;
  sneak: boolean;
};

export const EMPTY_PAD: PadSample = {
  moveX: 0,
  moveZ: 0,
  lookX: 0,
  lookY: 0,
  jump: false,
  sneak: false,
};

function axisAt(axes: ArrayLike<number>, index: number): number {
  const value = axes[index];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function buttonOn(buttons: ArrayLike<PadButtonLike>, index: number): boolean {
  const button = buttons[index];
  if (button == null) {
    return false;
  }
  if (typeof button === 'boolean') {
    return button;
  }
  if (typeof button === 'number') {
    return Number.isFinite(button) && button >= 0.5;
  }
  if (button.pressed === true) {
    return true;
  }
  return typeof button.value === 'number' && Number.isFinite(button.value) && button.value >= 0.5;
}

function radialDeadzone(x: number, y: number, dead: number): { x: number; y: number } {
  const mag = Math.hypot(x, y);
  if (mag <= dead || mag === 0) {
    return { x: 0, y: 0 };
  }
  const scaled = Math.min(1, (mag - dead) / (1 - dead)) / mag;
  return { x: x * scaled, y: y * scaled };
}

function noNegZero(value: number): number {
  return value === 0 ? 0 : value;
}

/** Left stick move, right stick look, A jump, LB sneak. Idle sticks leave WASD in charge. */
export function applyPad(axes: ArrayLike<number>, buttons: ArrayLike<PadButtonLike>): PadSample {
  const left = radialDeadzone(axisAt(axes, PAD_AXIS_LX), axisAt(axes, PAD_AXIS_LY), PAD_DEADZONE);
  const right = radialDeadzone(axisAt(axes, PAD_AXIS_RX), axisAt(axes, PAD_AXIS_RY), PAD_DEADZONE);
  return {
    moveX: noNegZero(left.x),
    moveZ: noNegZero(-left.y),
    lookX: noNegZero(right.x),
    lookY: noNegZero(right.y),
    jump: buttonOn(buttons, PAD_SOUTH),
    sneak: buttonOn(buttons, PAD_LB),
  };
}

export class InputMap {
  private readonly keys = new Set<string>();
  private readonly pressed = new Set<string>();
  private lookX = 0;
  private lookY = 0;
  private locked = false;
  private lockTarget: Element | null = null;
  private disposed = false;
  private pad: PadSample = { ...EMPTY_PAD };
  private padGen = 0;
  private lastPadGen = -1;
  private padJumpHeld = false;
  private padJumpEdge = false;
  private attackEdge = false;
  private attackHeld = false;
  private aimHeld = false;

  constructor() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('pointerlockchange', this.onLockChange);
    document.addEventListener('pointerlockerror', this.onLockError);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('pointerdown', this.onPointerDown);
    document.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('contextmenu', this.onContextMenu);
  }

  /**
   * WASD stays live without pointer lock so debug/QA can drive the capsule.
   * Mouse look is lock-only. Idle pad leaves WASD in charge.
   */
  get moveX(): number {
    this.pollPad();
    const key = (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0);
    return this.pad.moveX !== 0 || this.pad.moveZ !== 0 ? this.pad.moveX : key;
  }

  get moveZ(): number {
    this.pollPad();
    const key = (this.keys.has('KeyW') ? 1 : 0) - (this.keys.has('KeyS') ? 1 : 0);
    return this.pad.moveX !== 0 || this.pad.moveZ !== 0 ? this.pad.moveZ : key;
  }

  get sprint(): boolean {
    return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
  }

  /** Space or pad A held — swim up. */
  get jumpDown(): boolean {
    this.pollPad();
    return this.keys.has('Space') || this.pad.jump;
  }

  /** Hold C or pad LB. Not cover. In water this is sink. */
  get sneak(): boolean {
    this.pollPad();
    return this.keys.has('KeyC') || this.pad.sneak;
  }

  get pointerLocked(): boolean {
    return this.locked;
  }

  consumeLook(): { x: number; y: number } {
    this.padGen += 1;
    this.pollPad();
    this.lookX += this.pad.lookX * PAD_LOOK_SCALE;
    this.lookY += this.pad.lookY * PAD_LOOK_SCALE;
    const x = this.lookX;
    const y = this.lookY;
    this.lookX = 0;
    this.lookY = 0;
    return { x, y };
  }

  takeKey(code: string): boolean {
    const hit = this.pressed.has(code);
    this.pressed.delete(code);
    return hit;
  }

  /** Space or pad A. Edge. */
  consumeJump(): boolean {
    this.pollPad();
    return this.takeKey('Space') || this.takePadJump();
  }

  /** Keyboard E. Edge. */
  consumeInteract(): boolean {
    return this.takeKey('KeyE');
  }

  /** Keyboard B. Edge. Lean-to ghost / commit. */
  consumeBuild(): boolean {
    return this.takeKey('KeyB');
  }

  /** Keyboard F. Edge. Light campfire. */
  consumeFire(): boolean {
    return this.takeKey('KeyF');
  }

  /** Keyboard G. Edge. Craft bow when the schematic is known. */
  consumeCraft(): boolean {
    return this.takeKey('KeyG');
  }

  /** Keyboard Q. Edge. Cycle build recipe. */
  consumeCycle(): boolean {
    return this.takeKey('KeyQ');
  }

  /** Escape. Edge. Close the build menu. Does not preventDefault so pointer-lock can still unlock. */
  consumeCancel(): boolean {
    return this.takeKey('Escape');
  }

  /** Digit 1–3. Hotbar slots 0–2. */
  consumeHotbar(): number | null {
    if (this.takeKey('Digit1')) {
      return 0;
    }
    if (this.takeKey('Digit2')) {
      return 1;
    }
    if (this.takeKey('Digit3')) {
      return 2;
    }
    return null;
  }

  /** Pointer button 0. Edge. Spear thrust. */
  consumeAttack(): boolean {
    const hit = this.attackEdge;
    this.attackEdge = false;
    return hit;
  }

  /** LMB held — bow draw. */
  get attackDown(): boolean {
    return this.attackHeld;
  }

  /** RMB held — bow draw. */
  get aimDown(): boolean {
    return this.aimHeld;
  }

  /** E held — lean-to sleep charge. */
  get interactDown(): boolean {
    return this.keys.has('KeyE');
  }

  requestLock(): void {
    const canvas = document.querySelector('#game-root canvas');
    const target = canvas instanceof HTMLElement ? canvas : document.body;
    this.lockTarget = target;
    try {
      const pending = target.requestPointerLock();
      if (pending && typeof pending.catch === 'function') {
        void pending.catch(() => this.clearLock());
      }
    } catch {
      this.clearLock();
    }
  }

  ingestPad(axes: ArrayLike<number>, buttons: ArrayLike<PadButtonLike>): PadSample {
    this.lastPadGen = this.padGen;
    return this.applyPadSample(applyPad(axes, buttons));
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    document.removeEventListener('pointerlockerror', this.onLockError);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('contextmenu', this.onContextMenu);
    this.attackEdge = false;
    this.attackHeld = false;
    this.aimHeld = false;
    this.keys.clear();
    this.pressed.clear();
    this.lookX = 0;
    this.lookY = 0;
    this.locked = false;
    this.lockTarget = null;
    this.clearPad();
  }

  private pollPad(): void {
    if (this.lastPadGen === this.padGen) {
      return;
    }
    this.lastPadGen = this.padGen;
    this.applyPadSample(this.readLivePad());
  }

  private readLivePad(): PadSample {
    const nav = globalThis.navigator;
    if (!nav || typeof nav.getGamepads !== 'function') {
      return { ...EMPTY_PAD };
    }
    let pads: (Gamepad | null)[];
    try {
      pads = Array.from(nav.getGamepads());
    } catch {
      return { ...EMPTY_PAD };
    }
    const pad = pads.find((entry) => entry !== null && entry.connected);
    if (!pad) {
      return { ...EMPTY_PAD };
    }
    return applyPad(pad.axes, pad.buttons);
  }

  private applyPadSample(sample: PadSample): PadSample {
    this.pad = sample;
    if (sample.jump && !this.padJumpHeld) {
      this.padJumpEdge = true;
    }
    this.padJumpHeld = sample.jump;
    return sample;
  }

  private takePadJump(): boolean {
    const hit = this.padJumpEdge;
    this.padJumpEdge = false;
    return hit;
  }

  private clearPad(): void {
    this.pad = { ...EMPTY_PAD };
    this.padJumpHeld = false;
    this.padJumpEdge = false;
    this.lastPadGen = -1;
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    const target = event.target;
    if (target instanceof Element && target.closest('#boot-splash, #boot-start, button, a, input, #ui-root')) {
      return;
    }
    if (event.button === 0) {
      this.attackEdge = true;
      this.attackHeld = true;
    }
    if (event.button === 2) {
      this.aimHeld = true;
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.button === 0) {
      this.attackHeld = false;
    }
    if (event.button === 2) {
      this.aimHeld = false;
    }
  };

  private readonly onMouseDown = (event: MouseEvent): void => {
    if (this.locked) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('#boot-start, #game-root canvas')) {
      this.requestLock();
    }
  };

  private readonly onLockChange = (): void => {
    const owner = document.pointerLockElement;
    this.locked = owner !== null && (this.lockTarget === null || owner === this.lockTarget);
    if (!this.locked) {
      this.lookX = 0;
      this.lookY = 0;
    }
  };

  private readonly onLockError = (): void => {
    this.clearLock();
  };

  private clearLock(): void {
    this.locked = false;
    this.lookX = 0;
    this.lookY = 0;
  }

  private readonly onContextMenu = (event: MouseEvent): void => {
    if (this.locked) {
      event.preventDefault();
    }
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.locked) {
      return;
    }
    this.lookX += event.movementX;
    this.lookY += event.movementY;
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const code = digitCode(event);
    if (code === 'Space' || code === 'KeyC' || code === 'KeyF' || code === 'KeyB' || code === 'KeyE') {
      event.preventDefault();
    }
    if (!this.keys.has(code)) {
      this.pressed.add(code);
    }
    this.keys.add(code);
    if (code !== event.code && !this.keys.has(event.code)) {
      this.pressed.add(event.code);
      this.keys.add(event.code);
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
    this.keys.delete(digitCode(event));
  };

  private readonly onBlur = (): void => {
    this.keys.clear();
    this.pressed.clear();
    this.attackEdge = false;
    this.attackHeld = false;
    this.aimHeld = false;
    this.clearPad();
  };
}
